const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Cleans up a string to be used as a slug.
 * @param str Any string to be slugified.
 * @returns A slugified string.
 */
export const slugify = (str?:any) => str == undefined ? '' : str
	.toString() // Ensure the input is a string
	.normalize("NFD") // Normalize to decomposed form (e.g., é -> e + ́ )
  .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks (accents, etc.)
	.trim() // Remove whitespace from both ends of the string
	.toLowerCase() // Convert to lowercase
	.replace(/\s+/g, '-') // Replace spaces with hyphens
	.replace(/_/g, '-') // Replace underscores with hyphens
	.replace(/[^\w-]+/g, '') // Remove all non-word characters (except hyphens)
	.replace(/--+/g, '-') // Replace multiple hyphens with a single hyphen
	.replace(/^-+/, '') // Trim hyphens from the start of the string
	.replace(/-+$/, ''); // Trim hyphens from the end of the string

/**
* Capitalises first word and turns line-characters (-, _) into spaces
* @param str Any string to be unslugified.
* @returns A slugified string.
*/
export const unslugify = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)
	.toLowerCase()
	.replaceAll('-', ' ')
	.replaceAll('_', ' ');


/**
 * Cleans up a string to be used in CMS inserts ID
 * @param str Any string to be template slugified.
 * @returns A template slugified string.
 */
export const templatePathSlugify = (str?:any) => str == undefined ? '' : str
	.toString() // Ensure the input is a string
	.normalize("NFD") // Normalize to decomposed form (e.g., é -> e + ́ )
  .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks (accents, etc.)
	.trim() // Remove whitespace from both ends of the string
	.toLowerCase() // Convert to lowercase
	.replace(/\s+/g, '-') // Replace spaces with hyphens
	.replace(/_/g, '-') // Replace underscores with hyphens
	.replace(/[^\w-]+/g, '-') // Remove all non-word characters (except hyphens)
	.replace(/--+/g, '-') // Replace multiple hyphens with a single hyphen
	.replace(/^-+/, '') // Trim hyphens from the start of the string
	.replace(/-+$/, ''); // Trim hyphens from the end of the string

/**
 * This function existed in a few places risking inconsistency. It is now centralized here.
 * @param name Merchant name
 * @param suburb Merchant suburb
 * @returns A slugified string.
 */
export const merchantNameSuburbSlug = (name?: string, suburb?: string) => {
	return `${name && slugify(name) || 'mrd'}-${suburb && slugify(suburb) || 'near-me'}`;
}

/**
 * Format new Date() into API date requirement
 * @param date a Date object
 * @param spaced if true, sets format to "04 Jan 2024"
 * @returns A string eg 2023-09-27.
 */
export const toYYYYMMDD = (d: Date) => {
	const toIso = d.toISOString();
	return toIso.substring(0,toIso.indexOf("T"));
}

/**
 * Format new Date() into human readable string
 * @param date a Date object
 * @returns A string "04 Jan 2024"
 */
export const formateDateForHumans = (d: Date) => {

	const date = d.toLocaleDateString('en-ZA', {
		day: 'numeric',
		month: 'short',
		year: 'numeric'
	});

	return date;
}

/**
 * Format endTime and Startime into how many 'Remaining time' is left between order created and delivery
 * @param endTime A string for `Date.parse()`.
 * @param startTime A string for `Date.parse()`.
 * @returns Minutes remaining between endTime and startTime.
 */
export function getDurationInMinutes(endTime?: string, startTime?: string) {
	if (!endTime || !startTime) return null;
  const total = Date.parse(endTime) - Date.parse(startTime);
  // const seconds = Math.floor( (total/1000) % 60 );
  const minutes = Math.floor( (total/1000/60) % 60 );
  // const hours = Math.floor( (total/(1000*60*60)) % 24 );
  // const days = Math.floor( total/(1000*60*60*24) );
	return minutes;
}

/**
 * Format new Date() into timestamp
 * @param date a date string
 * @param hasDate if true, returns the time including the month and day.
 * @returns A string as '9:30' or as 'Jun 18, 9:30'
 */
export function dateTimeStamp(date?: string|number, hasDate?: boolean) {
	if (!date) return '...'; // edge case?
	const d = new Date(date);
	const h = d.getHours();
	const m = (d.getMinutes() < 10 ? '0' : '') + d.getMinutes();
	const day = d.getDate();
	const month = MONTH_SHORT[d.getMonth()];
	return hasDate ? `${month} ${day}, ${h}:${m}` : `${h}:${m}`;
}

/**
 * Convert text to camel case.
 * @param text The text to convert.
 * @returns 
 */
export function camCase(text:string) {
	if (!text || !text.length) return '';
	return text[0].toUpperCase() + text.slice(1);
}

/**
 * Capitalizes the first letter of a string.
 * @param str The string to capitalize.
 * @returns The string with the first letter capitalized.
 */
export function capitalizeFirstLetter(str: string): string {
	if (!str || str.length === 0) return str;
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export const formatCurrency = (number?: number): string => {
	if (!number) return 'R0';
	const minus = number < 0 ? '-' : '';
	return Math.abs(number)
		.toFixed(2)
		.replace(/^/, `${minus}R`);
}


// for restaurants
export function formatFoodReadyTime(number: number) {
	const remaining = number % 60
	const hours = ~~(number / 60)
	if (number === 1) return number + ' min'
	if (number < 60) return number + ' mins'
	if (number === 60) return `${hours} hour`
	if (number > 60) {
		if (remaining === 0) return `${hours} hours`
		if (remaining === 1 && hours === 1) return `${hours} hour ${remaining} min`
		if (remaining === 1 && hours > 1) return `${hours} hours ${remaining} min`
		if (hours === 1) return `${hours} hour ${remaining} mins`
		return `${hours} hours ${remaining} mins`
	}
}

/**
 * Used to convert object values to strings for use in query params. Often used before
 * calling `new URLSearchParams()`.
 * @param obj Any immutable object with preferably primitive values.
 * @returns An new object with all values converted to strings.
 */
export function parseObjectValuesToString(obj?: object | Record<string, unknown> | null):Record<string, string> {
	const newObj: Record<string, string> = {};
	if (!obj) return newObj;
	for (const [key, value] of Object.entries(obj)) {
		switch (typeof value) {
			case 'number':
			case 'boolean':
			case 'bigint':
				newObj[key] = value.toString();
				break;
			
			case 'string':
				newObj[key] = value;
				break;
			
			case 'object':
				if (value === null) {
					// we are not interested in null values
				} else if (Array.isArray(value)) {
					newObj[key] = value.filter(i => i === null || typeof i === 'number' || typeof i === 'string' || typeof i === 'boolean' || typeof i === 'bigint').join(',');
				} else if (value instanceof Date) {
					newObj[key] = value.toISOString();
				}
				break;
		}
	}
	// newObj.toString = () => Object.entries(newObj).filter((v,k) => typeof v !== 'function').map(([key, value]) => `${key}=${value}`).join('&');
	return newObj;
}

/**
 * Get the error message from an error object.
 * @param error Any unknown error object or string.
 * @param def A default string to return if a message cannot be extracted.
 * @returns A string message extracted from the error object.
 */
export function extractErrorMessage(error: unknown, def = 'Oops'): string {
	let str: string | undefined;
	if (error === null || typeof error === 'undefined') return def;
	if (typeof error === "string") return error;
	if (error instanceof Error) return error.message;
	if (typeof error === "object") {
		let obj = error as any;
		obj = obj?.error ?? obj;
		str = obj?.friendly_message ?? obj?.message ?? obj?.msg ?? obj?.error_message ?? obj?.developer_message;

	}
	return str ?? def;
}

/**
 * Generate a random number between 0 and 1 using the splitmix32 algorithm.
 * @param a Seed for the random number generator.
 * @returns A function that generates a random number between 0 and 1.
 */
export function splitmix32(a: number) {
	return function() {
		a |= 0;
		a = a + 0x9e3779b9 | 0;
		let t = a ^ a >>> 16;
		t = Math.imul(t, 0x21f0aaad);
		t = t ^ t >>> 15;
		t = Math.imul(t, 0x735a2d97);
		return ((t = t ^ t >>> 15) >>> 0) / 4294967296;
	}
}

/**
 * Generate a random integer between 0 and 2^32.
 * @returns A random integer between 0 and 2^32.
 */
export const rndi = () => (Math.random()*2**32)>>>0;
